import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    if (host.includes('localhost') || host.includes('127.0.0.1') || host.includes('.replit.dev')) {
      return `${protocol}//${host}`;
    }
  }
  return 'https://brainwrk.ai';
};

const DEFAULT_TITLE = 'BrainWrk - Unified Knowledge OS';
const DEFAULT_DESCRIPTION = 'Transform your team training with AI-powered learning paths. BrainWrk delivers measurable ROI through adaptive, engaging learning experiences.';

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  noIndex = false,
  structuredData,
}: SEOProps) {
  const baseUrl = getBaseUrl();
  const fullTitle = title ? `${title} | BrainWrk` : DEFAULT_TITLE;
  const fullCanonicalUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : undefined;
  const resolvedOgImage = ogImage || `${baseUrl}/og-image.png`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={resolvedOgImage} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:site_name" content="BrainWrk" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
